import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import { Plus, Search, FileDown, Pencil, Trash2 } from "lucide-react";

interface AwardItem {
  id: string;
  stt: number;
  functionType: string;
  awardContent: string;
  collective: string;
  individual: string;
  awardTime: string;
  awardFund: string;
  fundSource: string;
  basis: string;
  evidence: string;
  notes: string;
}

const mockData: AwardItem[] = [
  {
    id: "1",
    stt: 1,
    functionType: "- Chọn -",
    awardContent: "Bằng khen của Bộ trưởng Bộ Khoa học và Công nghệ",
    collective: "Viện Khoa học Thủy lợi Việt Nam",
    individual: "Nguyễn Văn A",
    awardTime: "2023",
    awardFund: "10.000.000 đ",
    fundSource: "Ngân sách nhà nước",
    basis: "Quyết định số 123/QĐ-BKHCN",
    evidence: "Bằng khen gốc",
    notes: "",
  },
  {
    id: "2",
    stt: 2,
    functionType: "- Chọn -",
    awardContent: "Huân chương Lao động hạng Ba",
    collective: "",
    individual: "Trần Thị B",
    awardTime: "2022",
    awardFund: "5.000.000 đ",
    fundSource: "Ngân sách nhà nước",
    basis: "Nghị định 98/2020/NĐ-CP",
    evidence: "Huân chương gốc",
    notes: "Thành tích xuất sắc trong nghiên cứu khoa học",
  },
];

const Awards: React.FC = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [awardToDelete, setAwardToDelete] = useState<AwardItem | null>(null);

  return (
    <PageShell title="Khen thưởng">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">Khen thưởng</h1>
          <button
            onClick={() => navigate("/add-new-award")}
            className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Thêm mới Khen thưởng</span>
          </button>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-4">
            {/* Row 1 - Search input + Search button */}
            <div className="flex items-center space-x-4">
              <div className="relative w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Tìm kiếm khen thưởng"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors">
                Tìm kiếm
              </button>
            </div>

            {/* Row 2 - Filters */}
            <div className="flex space-x-4 items-center">
              <div className="relative">
                <input
                  type="date"
                  defaultValue="2025-03-10"
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent w-40"
                />
              </div>
              <div className="relative">
                <input
                  type="date"
                  defaultValue="2026-03-10"
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent w-40"
                />
              </div>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Nguồn kinh phí -</option>
                <option>Ngân sách nhà nước</option>
                <option>Ngân sách địa phương</option>
                <option>Tự có</option>
                <option>Doanh nghiệp tài trợ</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Tập thể -</option>
                <option>Viện Khoa học Thủy lợi Việt Nam</option>
                <option>Viện Kỹ thuật Biển</option>
                <option>Phòng Vật liệu</option>
                <option>Khoa CNTT</option>
              </select>
            </div>

            {/* Row 3 - Export button */}
            <div className="flex justify-start">
              <button className="bg-orange-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors flex items-center space-x-2">
                <FileDown className="h-4 w-4" />
                <span>Xuất ra file</span>
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    STT
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-64">
                    Nội dung khen thưởng
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-48">
                    Tập thể
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-48">
                    Cá nhân
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Thời gian nhận khen thưởng
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Kinh phí khen thưởng
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Nguồn kinh phí
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-48">
                    Căn cứ
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Minh chứng
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Ghi chú
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider w-24">
                    Chức năng
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 w-16">
                      {item.stt}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-64">
                      <div className="font-medium text-[#337ab7] text-[14px] leading-tight underline">
                        {item.awardContent} »
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-48">
                      {item.collective}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-48">
                      {item.individual}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-32 text-center">
                      {item.awardTime}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-32">
                      {item.awardFund}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-32">
                      {item.fundSource}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-48">
                      {item.basis}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-32">
                      {item.evidence}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-32">
                      {item.notes}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap w-24">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          aria-label="Sửa"
                          className="p-1 rounded text-[#337ab7] hover:bg-blue-50 hover:text-[#2e6da4] transition-colors"
                          onClick={() => {
                            navigate(`/edit-award/${item.id}`);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          aria-label="Xóa"
                          className="p-1 rounded text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                          onClick={() => {
                            setAwardToDelete(item);
                            setShowDeleteConfirm(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination inside the table container */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700">
              Hiển thị <span className="font-bold">1</span> đến{" "}
              <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                <option>10</option>
                <option>20</option>
                <option>50</option>
                <option>100</option>
              </select>{" "}
              trong <span className="font-bold">{mockData.length}</span> kết quả
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &lt;
              </button>
              <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                1
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &gt;
              </button>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
            <div className="bg-white rounded-md p-4 max-w-sm w-full mx-4">
              <h3 className="text-base font-semibold text-gray-900 mb-2">
                Xác nhận xóa
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Bạn có chắc muốn xóa khen thưởng{" "}
                <span className="font-semibold text-gray-900">
                  {awardToDelete?.awardContent}
                </span>
                ? Hành động này không thể hoàn tác.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setAwardToDelete(null);
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Hủy
                </button>
                <button
                  onClick={() => {
                    if (awardToDelete) {
                      // Here you would implement the actual delete logic
                      console.log("Deleting award:", awardToDelete.id);
                    }
                    setShowDeleteConfirm(false);
                    setAwardToDelete(null);
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
                >
                  Xóa
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default Awards;
